import React, { forwardRef, useImperativeHandle, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { WebService } from "../../Services/WebService";
import { StandardConst } from "../../Services/StandardConst";
import {
  PersonFill,
  TelephoneFill,
  EnvelopeFill,
  GeoAltFill,
  CalendarEventFill,
  ChatLeftTextFill,
  Building,
} from "react-bootstrap-icons";

const ViewSalesVisit = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const [show, setShow] = useState(false);
  const [details, setDetails] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [zoomPhoto, setZoomPhoto] = useState(null);

  function normalizeKeys(obj) {
    if (!obj) return obj;
    const newObj = { ...obj };
    Object.keys(obj).forEach((key) => {
      newObj[key.toLowerCase()] = obj[key];
    });
    return newObj;
  }

  useImperativeHandle(ref, () => ({
    openModal: async (data) => {
      const normalized = normalizeKeys(data);
      console.log("---", normalized);
      setDetails(normalized);
      setShow(true);

      const id = normalized.visitid;
      if (id) {
        try {
          const imagesData = await WebService({
            endPoint: `CommonUtility/fetchAll/VisitImages?VisitID=${id}`,
            dispatch,
          });
          setPhotos(imagesData);
        } catch (err) {
          console.error("Error fetching visit images", err);
        }
      }
    },
  }));

  const handleClose = () => {
    setShow(false);
    setDetails(null);
    setPhotos([]);
    setZoomPhoto(null);
  };

  if (!details) return null;

  const profilePhoto =
    photos.length > 0
      ? photos[0]?.previewUrl ||
        `${StandardConst.apiBaseUrl}/uploads/${photos[0].ImageURL}`
      : null;

  return (
    <>
      <Modal show={show} onHide={handleClose} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>📝 Customer Visit Details</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 bg-light">
          {/* Info Card */}
          <div className="card shadow-sm border-0 rounded-3 mb-3 mt-4">
            <div className="card-body">
              <div className="row gy-3">
                <div className="col-md-6">
                  <Building className="me-2 text-primary" />
                  <b>Business:</b> {details.bussinessname}
                </div>
                <div className="col-md-6">
                  <PersonFill className="me-2 text-success" />
                  <b>Contact:</b> {details.name || details.retailer || "N/A"}
                </div>
                <div className="col-md-6">
                  <TelephoneFill className="me-2 text-info" />
                  <b>Phone:</b> {details.phone}
                </div>
                <div className="col-md-6">
                  <EnvelopeFill className="me-2 text-danger" />
                  <b>Email:</b> {details.email}
                </div>
                <div className="col-md-12">
                  <GeoAltFill className="me-2 text-warning" />
                  <b>Address:</b> {details.address || "N/A"}
                </div>
                <div className="col-md-6">
                  <CalendarEventFill className="me-2 text-primary" />
                  <b>purpose:</b> {details.purpose}
                </div>
                <div className="col-md-6">
                  🌍 <b>Latitude:</b> {details.locationlat || "N/A"}
                </div>
                <div className="col-md-6">
                  🌍 <b>Longitude:</b> {details.locationlong || "N/A"}
                </div>
                <div className="col-md-6">
                  <CalendarEventFill className="me-2 text-primary" />
                  <b>Scheduled:</b>{" "}
                  {details.scheduleddate
                    ? new Date(details.scheduleddate).toLocaleString("en-IN", {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                        hour12: true, // 👈 ensures AM/PM
                      })
                    : "NA"}
                </div>

                <div className="col-md-12">
                  <ChatLeftTextFill className="me-2 text-muted" />
                  <b>Remarks:</b> {details.remarks || "N/A"}
                </div>
              </div>
            </div>
          </div>

          {/* Row for Photo(s) + Map */}
          <div className="row">
            {/* Photos Column */}
            <div className="col-md-6">
              <b>📸 Photos</b>
              <div className="d-flex flex-wrap gap-2 mt-2">
                {photos.length > 0 ? (
                  photos.map((photo, i) => {
                    const photoUrl =
                      photo?.previewUrl ||
                      `${StandardConst.apiBaseUrl}/uploads/${photo.ImageURL}`;

                    return (
                      <div
                        key={i}
                        style={{
                          borderRadius: "10px",
                          overflow: "hidden",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                          cursor: "pointer",
                        }}
                        onClick={() => setZoomPhoto(photoUrl)}
                      >
                        <img
                          src={photoUrl}
                          alt={`Selfie ${i + 1}`}
                          style={{
                            width: "100px",
                            height: "100px",
                            objectFit: "cover",
                          }}
                        />
                      </div>
                    );
                  })
                ) : (
                  <p>No photos available.</p>
                )}
              </div>
            </div>

            {/* Map Column */}
            <div className="col-md-6">
              {details.locationlat && details.locationlong ? (
                <>
                  <b>📍 Location Map</b>
                  <div
                    className="mt-2 rounded overflow-hidden shadow-sm"
                    style={{ height: "300px" }}
                  >
                    <iframe
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      loading="lazy"
                      allowFullScreen
                      src={`https://www.google.com/maps?q=${details.locationlat},${details.locationlong}&hl=es;z=14&output=embed`}
                    ></iframe>
                  </div>
                </>
              ) : (
                <p>No location available.</p>
              )}
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Zoom Overlay */}
      {zoomPhoto && (
        <div
          onClick={() => setZoomPhoto(null)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.8)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            cursor: "zoom-out",
          }}
        >
          <img
            src={zoomPhoto}
            alt="Zoomed"
            style={{
              maxWidth: "90%",
              maxHeight: "90%",
              borderRadius: "12px",
              boxShadow: "0 0 25px rgba(0,0,0,0.6)",
            }}
          />
        </div>
      )}
    </>
  );
});

export default ViewSalesVisit;
